import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PendingPaymentModal from '@/components/PendingPaymentModal';
import PlanExpiredOverlay from '@/components/PlanExpiredOverlay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Rutas públicas o de flujo de pago/renovación donde NO bloqueamos:
 * - Páginas públicas, login, register, reset, etc.
 * - Flujo de pago (checkout, success, urls fijas de MercadoPago).
 * - Páginas internas para renovar/pagar (/renewal).
 * - Panel admin.
 */
const PUBLIC_PATHS = [
  '/',
  '/login',
  '/register',
  '/reset-password',
  '/admin-access',
  '/test-login',
  '/demo-login',
  '/payment-success',
  '/payment-cancel',
  '/pago/exito',
  '/pago/error',
  '/pago/pendiente',
  '/renewal',
];

const PUBLIC_PREFIXES = [
  '/checkout',
  '/admin',
  '/plans',
];

function isExcludedPath(pathname) {
  if (PUBLIC_PATHS.includes(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`) || pathname.startsWith(`${p}-`));
}

/**
 * Wrapper global que cubre dos bloqueos para TODAS las rutas protegidas:
 *  1. Pago pendiente inicial (clienta VIP nueva sin pagar).
 *  2. Plan totalmente vencido sin días de gracia (debe renovar).
 * Excluye admin y rutas públicas/de pago/renovación.
 */
export default function GlobalPaymentGate({ isAuthenticated, user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('deisycfit_token');
      const res = await axios.get(`${API}/auth/profile`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setProfile(res.data || null);
    } catch (e) {
      setProfile(null);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
    } else {
      setProfile(null);
    }
  }, [isAuthenticated, fetchProfile]);

  // Refrescar perfil al cambiar de ruta
  useEffect(() => {
    if (isAuthenticated) fetchProfile();
  }, [location.pathname, isAuthenticated, fetchProfile]);

  if (!isAuthenticated) return null;
  if (user?.role === 'admin' || profile?.is_admin) return null;
  if (isExcludedPath(location.pathname)) return null;

  // 1) Pago pendiente inicial -> modal bloqueante
  if (profile?.payment_status === 'pending') {
    return <PendingPaymentModal user={user} profile={profile} />;
  }

  // 2) Plan totalmente vencido -> overlay de renovación
  const isFullyExpired =
    profile?.is_fully_expired === true &&
    typeof profile?.days_until_expiry === 'number' &&
    profile.days_until_expiry < 0;

  if (isFullyExpired) {
    const goCheckoutRenewal = () => {
      const p = new URLSearchParams();
      p.set('renewal', '1');
      p.set('plan', profile.subscription_plan || 'premium');
      p.set('period', profile.subscription_period || 'monthly');
      const amount = profile.renewal_price || profile.subscription_price;
      if (amount) p.set('amount', String(amount));
      if (profile.is_vip_price) p.set('vip', '1');
      navigate(`/checkout?${p.toString()}`);
    };
    return (
      <div className="fixed inset-0 z-[180]" style={{ backgroundColor: '#0a0a0a', overflowY: 'auto' }}>
        <PlanExpiredOverlay
          graceDaysRemaining={0}
          isFullyExpired={true}
          planType={profile.subscription_plan || 'premium'}
          renewalPrice={profile.renewal_price || profile.subscription_price}
          isVipPrice={profile.is_vip_price || false}
          vipSpecialPrice={profile.is_vip_price ? profile.renewal_price : null}
          vipPeriod={profile.vip_period || null}
          subscriptionPeriod={profile.subscription_period || 'mensual'}
          onRenew={goCheckoutRenewal}
          onPayOnline={goCheckoutRenewal}
        />
      </div>
    );
  }

  return null;
}
